import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'react-router-dom';
import { motion, useAnimationControls } from 'motion/react';
import clsx from 'clsx';
import { trackConversion } from '@/utils/analytics';
import { CalendlyEmbed, CALENDLY_DEMO_URL } from './CalendlyEmbed';
import {
  demoFormSchema,
  contactFormSchema,
  newsletterSchema,
  type DemoFormInput,
  type ContactFormInput,
  type NewsletterFormData,
  INDUSTRIES,
  INDUSTRY_LABELS,
  COMPANY_SIZES,
  INQUIRY_TYPES,
  INQUIRY_TYPE_LABELS,
} from '@/types/forms';

type FormMode = 'demo' | 'contact' | 'newsletter';

interface LeadFormProps {
  mode: FormMode;
  className?: string;
  onSuccess?: () => void;
  defaultIndustry?: string | null;
}

export function LeadForm({ mode, className, onSuccess, defaultIndustry }: LeadFormProps) {
  const [formLoadTime] = useState(Date.now());

  if (mode === 'demo') {
    return <DemoForm className={className} onSuccess={onSuccess} formLoadTime={formLoadTime} defaultIndustry={defaultIndustry} />;
  }

  if (mode === 'contact') {
    return <ContactForm className={className} onSuccess={onSuccess} formLoadTime={formLoadTime} />;
  }

  return <NewsletterForm className={className} onSuccess={onSuccess} />;
}

// Animated form field wrapper
function AnimatedField({
  children,
  hasError
}: {
  children: React.ReactNode;
  hasError: boolean;
}) {
  const controls = useAnimationControls();

  useEffect(() => {
    if (hasError) {
      controls.start({
        x: [0, -8, 8, -8, 8, 0],
        transition: { duration: 0.4 },
      });
    }
  }, [hasError, controls]);

  return (
    <motion.div animate={controls}>
      {children}
    </motion.div>
  );
}

// Demo Request Form
function DemoForm({
  className,
  onSuccess,
  formLoadTime,
  defaultIndustry,
}: {
  className?: string;
  onSuccess?: () => void;
  formLoadTime: number;
  defaultIndustry?: string | null;
}) {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submittedData, setSubmittedData] = useState<DemoFormInput | null>(null);

  // Capture UTM parameters
  const utmData = {
    utmSource: searchParams.get('utm_source') || '',
    utmMedium: searchParams.get('utm_medium') || '',
    utmCampaign: searchParams.get('utm_campaign') || '',
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<DemoFormInput>({
    resolver: zodResolver(demoFormSchema),
    mode: 'onBlur', // Validate on field blur
    reValidateMode: 'onChange', // Re-validate as user types after error
  });

  // Pre-populate industry from URL parameter
  useEffect(() => {
    const industry = defaultIndustry || searchParams.get('industry');
    if (industry && INDUSTRIES.includes(industry as typeof INDUSTRIES[number])) {
      setValue('industry', industry as typeof INDUSTRIES[number]);
    }
  }, [defaultIndustry, searchParams, setValue]);

  const onSubmit = async (data: DemoFormInput) => {
    // Time-based bot detection
    if (Date.now() - formLoadTime < 3000) {
      console.warn('Form submitted too quickly');
      return;
    }

    // Honeypot check
    if (data.website) {
      console.warn('Honeypot triggered');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/marketing/demo-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, ...utmData, formLoadTime }),
      });

      if (!response.ok) throw new Error('Failed to submit');

      // Track demo request conversion
      trackConversion('demo_request', undefined, {
        industry: data.industry,
        company_size: data.companySize,
      });

      // Store submitted data for Calendly pre-fill
      setSubmittedData(data);
      setSubmitStatus('success');
      reset();
      onSuccess?.();
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={clsx('', className)}
      >
        {/* Calendly Embed - use negative margin to break out of card padding */}
        <div className="-mx-6 md:-mx-8 -mt-6 md:-mt-8">
          <CalendlyEmbed
            url={CALENDLY_DEMO_URL}
            prefill={submittedData ? {
              name: submittedData.name,
              email: submittedData.email,
              phone: submittedData.phone,
              company: submittedData.company,
            } : undefined}
            utm={{
              utmSource: utmData.utmSource || undefined,
              utmMedium: utmData.utmMedium || undefined,
              utmCampaign: utmData.utmCampaign || undefined,
            }}
            minHeight={680}
          />
        </div>

        {/* TODO: Replace placeholder phone number when real number is available */}
        <div className="text-center pt-3 mt-2">
          <p className="text-xs text-muted mb-1">Prefer to talk now?</p>
          <a
            href="tel:+15551234567"
            className="text-accent font-semibold hover:underline text-sm"
          >
            Call us: (555) 123-4567
          </a>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={className}>
      {/* Screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {isSubmitting && 'Submitting form...'}
        {submitStatus === 'error' && 'Form submission failed. Please try again.'}
      </div>

      <div className="space-y-4">
        {/* Name */}
        <AnimatedField hasError={!!errors.name}>
          <label htmlFor="demo-name" className="block text-sm font-medium mb-1 text-gray-900">
            Full Name <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="demo-name"
            type="text"
            {...register('name')}
            aria-required="true"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'demo-name-error' : undefined}
            className={clsx('input', errors.name && 'input-error')}
            placeholder="John Smith"
          />
          {errors.name && (
            <p id="demo-name-error" className="error-message" role="alert">
              {errors.name.message}
            </p>
          )}
        </AnimatedField>

        {/* Email */}
        <AnimatedField hasError={!!errors.email}>
          <label htmlFor="demo-email" className="block text-sm font-medium mb-1 text-gray-900">
            Work Email <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="demo-email"
            type="email"
            {...register('email')}
            aria-required="true"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'demo-email-error' : undefined}
            className={clsx('input', errors.email && 'input-error')}
            placeholder="john@company.com"
          />
          {errors.email && (
            <p id="demo-email-error" className="error-message" role="alert">
              {errors.email.message}
            </p>
          )}
        </AnimatedField>

        {/* Phone */}
        <AnimatedField hasError={!!errors.phone}>
          <label htmlFor="demo-phone" className="block text-sm font-medium mb-1 text-gray-900">
            Phone Number <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="demo-phone"
            type="tel"
            {...register('phone')}
            aria-required="true"
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? 'demo-phone-error' : undefined}
            className={clsx('input', errors.phone && 'input-error')}
            placeholder="(555) 123-4567"
          />
          {errors.phone && (
            <p id="demo-phone-error" className="error-message" role="alert">
              {errors.phone.message}
            </p>
          )}
        </AnimatedField>

        {/* Company */}
        <AnimatedField hasError={!!errors.company}>
          <label htmlFor="demo-company" className="block text-sm font-medium mb-1 text-gray-900">
            Company Name <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="demo-company"
            type="text"
            {...register('company')}
            aria-required="true"
            aria-invalid={!!errors.company}
            aria-describedby={errors.company ? 'demo-company-error' : undefined}
            className={clsx('input', errors.company && 'input-error')}
            placeholder="Acme HVAC"
          />
          {errors.company && (
            <p id="demo-company-error" className="error-message" role="alert">
              {errors.company.message}
            </p>
          )}
        </AnimatedField>

        {/* Company Size */}
        <AnimatedField hasError={!!errors.companySize}>
          <label htmlFor="demo-size" className="block text-sm font-medium mb-1 text-gray-900">
            Company Size <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <select
            id="demo-size"
            {...register('companySize')}
            aria-required="true"
            aria-invalid={!!errors.companySize}
            aria-describedby={errors.companySize ? 'demo-size-error' : undefined}
            className={clsx('input', errors.companySize && 'input-error')}
          >
            <option value="">Select team size</option>
            {COMPANY_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} employees
              </option>
            ))}
          </select>
          {errors.companySize && (
            <p id="demo-size-error" className="error-message" role="alert">
              {errors.companySize.message}
            </p>
          )}
        </AnimatedField>

        {/* Industry (optional) */}
        <div>
          <label htmlFor="demo-industry" className="block text-sm font-medium mb-1 text-gray-900">
            Industry
          </label>
          <select id="demo-industry" {...register('industry')} className="input">
            <option value="">Select your industry</option>
            {INDUSTRIES.map((industry) => (
              <option key={industry} value={industry}>
                {INDUSTRY_LABELS[industry]}
              </option>
            ))}
          </select>
        </div>

        {/* Honeypot */}
        <div aria-hidden="true" className="sr-only">
          <input {...register('website')} tabIndex={-1} autoComplete="off" />
        </div>

        {submitStatus === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
            role="alert"
          >
            Something went wrong. Please try again or call us at (555) 123-4567.
          </motion.div>
        )}

        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting...' : 'Request Demo'}
        </motion.button>

        <p className="text-xs text-muted text-center">
          By submitting, you agree to our{' '}
          <a href="/privacy" className="link-accent">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </form>
  );
}

// Contact Form
function ContactForm({
  className,
  onSuccess,
  formLoadTime,
}: {
  className?: string;
  onSuccess?: () => void;
  formLoadTime: number;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormInput>({
    resolver: zodResolver(contactFormSchema),
  });

  const onSubmit = async (data: ContactFormInput) => {
    if (Date.now() - formLoadTime < 3000) return;
    if (data.website) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/marketing/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error('Failed to submit');

      // Track contact form conversion
      trackConversion('contact_form', undefined, {
        inquiry_type: data.inquiryType,
      });

      setSubmitStatus('success');
      reset();
      onSuccess?.();
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className={clsx('text-center py-8', className)}>
        <div className="w-16 h-16 rounded-full bg-success-light mx-auto mb-4 flex items-center justify-center">
          <CheckCircleIcon className="w-8 h-8 text-success" />
        </div>
        <h3 className="font-heading font-semibold text-xl mb-2">Message Sent!</h3>
        <p className="text-muted">
          Thanks for reaching out. We'll get back to you within one business day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={className}>
      <div className="space-y-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium mb-1 text-gray-900">
            Full Name *
          </label>
          <input
            id="contact-name"
            type="text"
            {...register('name')}
            className={clsx('input', errors.name && 'input-error')}
            placeholder="John Smith"
          />
          {errors.name && <p className="error-message">{errors.name.message}</p>}
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium mb-1 text-gray-900">
            Email *
          </label>
          <input
            id="contact-email"
            type="email"
            {...register('email')}
            className={clsx('input', errors.email && 'input-error')}
            placeholder="john@company.com"
          />
          {errors.email && <p className="error-message">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="contact-type" className="block text-sm font-medium mb-1 text-gray-900">
            Inquiry Type *
          </label>
          <select
            id="contact-type"
            {...register('inquiryType')}
            className={clsx('input', errors.inquiryType && 'input-error')}
          >
            <option value="">Select type</option>
            {INQUIRY_TYPES.map((type) => (
              <option key={type} value={type}>
                {INQUIRY_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
          {errors.inquiryType && <p className="error-message">{errors.inquiryType.message}</p>}
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium mb-1 text-gray-900">
            Message *
          </label>
          <textarea
            id="contact-message"
            {...register('message')}
            rows={4}
            className={clsx('input', errors.message && 'input-error')}
            placeholder="How can we help?"
          />
          {errors.message && <p className="error-message">{errors.message.message}</p>}
        </div>

        {/* Honeypot */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px' }}>
          <input {...register('website')} tabIndex={-1} autoComplete="off" />
        </div>

        {submitStatus === 'error' && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            Something went wrong. Please try again.
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </div>
    </form>
  );
}

// Newsletter Form
function NewsletterForm({
  className,
  onSuccess,
}: {
  className?: string;
  onSuccess?: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
  });

  const onSubmit = async (data: NewsletterFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/marketing/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error('Failed to submit');

      // Track newsletter signup conversion
      trackConversion('newsletter_signup');

      setSubmitStatus('success');
      reset();
      onSuccess?.();
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className={clsx('text-center', className)}>
        <p className="text-success font-medium">Thanks for subscribing!</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={className}>
      <div className="flex gap-2">
        <input
          type="email"
          {...register('email')}
          className={clsx('input flex-1', errors.email && 'input-error')}
          placeholder="Enter your email"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary px-6 disabled:opacity-50"
        >
          {isSubmitting ? '...' : 'Subscribe'}
        </button>
      </div>
      {errors.email && <p className="error-message mt-1">{errors.email.message}</p>}
    </form>
  );
}

// Check circle icon
function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
